import React from "react";
import { Download } from "lucide-react";

const MAIN_TITLE_PATTERN = /^MEMORANDO CONFIDENCIAL(?:\s*[—–-].*)?$/i;
const SECTION_PATTERN = /^[IVXLC]+\.\s+.+/;
const NAMED_SECTION_HEADINGS = new Set([
  "HISTORICO DO PROCESSO",
  "VIABILIDADE AQUISICAO",
  "DOCUMENTOS ANALISADOS",
]);
const METADATA_PATTERN =
  /^(Data|Assunto|Síntese Sentença|Síntese Acórdão|Responsabilidade subsidiária|Advogado Reclamante|Sentença|Acórdão|Cumprimento de Sentença|Autor|Advogado|Procuração|Réu|Terceiros|Síntese|Natureza|Data Expedição|LOA|Total Requisitado|Beneficiário Principal|Contratual|Honorários de Sucumbência|Valor Atualizado|Natureza do Crédito|Data Base|Data Final|Regime Principal|Bases Legais)\s*:/i;
const SUBHEADING_PATTERN =
  /^(Partes envolvidas|Valor da condenação|Checklist de impeditivos|Ofícios Requisitórios|PRECATÓRIO|RPV|Atualização de valores)\s*:?$/i;
const CLIENTE_LINE_PATTERN = /^Cliente\s*:/i;
const SUBITEM_PATTERN = /^\([ivx]+\)\s+/i;
const ORDINAL_ITEM_PATTERN = /^\d+º\s+/;
const CHECKLIST_ITEM_PATTERN = /^[-*]\s+/;
const DOCUMENT_BULLET_PATTERN = /^(.+?)\s—\s(.+?)\s—\sLink:\s*(.*)$/i;

const URL_PATTERN = /(https?:\/\/[^\s]+)/g;

function normalizeHeading(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();
}

function isSectionHeading(value) {
  if (SECTION_PATTERN.test(value)) return true;
  return NAMED_SECTION_HEADINGS.has(normalizeHeading(value));
}

function normalizeUrl(value) {
  const trimmed = String(value || "").trim().replace(/[.,;]+$/, "");
  return /^https?:\/\//i.test(trimmed) ? trimmed : null;
}

function renderDownloadLink(href, key) {
  return (
    <a
      key={key}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-2 py-0.5 hover:bg-blue-100 transition-colors no-underline"
    >
      Baixar
      <Download className="w-3.5 h-3.5" aria-hidden />
    </a>
  );
}

function formatInline(text, options = {}) {
  const raw = String(text);
  const linkLabel = options.linkLabel || null;
  const withBold = raw.split(/(\*\*[^*]+\*\*)/g);

  return withBold.flatMap((segment, segmentIndex) => {
    if (segment.startsWith("**") && segment.endsWith("**")) {
      return (
        <strong key={`b-${segmentIndex}`} className="font-semibold text-gray-900">
          {segment.slice(2, -2)}
        </strong>
      );
    }

    const urlParts = segment.split(URL_PATTERN);
    if (urlParts.length === 1) return segment;

    return urlParts.map((part, partIndex) => {
      if (/^https?:\/\//i.test(part)) {
        const href = part.replace(/[.,;]+$/, "");
        if (linkLabel) {
          return renderDownloadLink(href, `u-${segmentIndex}-${partIndex}`);
        }
        return (
          <a
            key={`u-${segmentIndex}-${partIndex}`}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-700 underline break-all hover:text-blue-900"
          >
            {href}
          </a>
        );
      }
      return part;
    });
  });
}

function renderMetadataLine(line, key) {
  const colonIndex = line.indexOf(":");
  if (colonIndex === -1) {
    return (
      <p key={key} className="text-sm text-gray-700 mb-1 leading-relaxed">
        {formatInline(line)}
      </p>
    );
  }

  const label = line.slice(0, colonIndex + 1);
  const value = line.slice(colonIndex + 1);

  return (
    <p key={key} className="text-sm text-gray-700 mb-1 leading-relaxed">
      <span className="font-semibold text-gray-900">{label}</span>
      {formatInline(value)}
    </p>
  );
}

function renderBulletLine(line, key) {
  const content = line.replace(CHECKLIST_ITEM_PATTERN, "");
  const documentMatch = content.match(DOCUMENT_BULLET_PATTERN);

  if (documentMatch) {
    const [, nome, data, linkRaw] = documentMatch;
    const href = normalizeUrl(linkRaw);

    return (
      <li key={key} className="text-sm text-gray-700 leading-relaxed">
        <span className="font-medium text-gray-900">{nome.trim()}</span>
        {" — "}
        {data.trim()}
        {" — "}
        {href ? renderDownloadLink(href, `${key}-download`) : (
          <span className="text-gray-500">Sem link</span>
        )}
      </li>
    );
  }

  const colonIndex = content.indexOf(":");

  if (colonIndex > 0 && colonIndex < 60) {
    const label = content.slice(0, colonIndex + 1);
    const value = content.slice(colonIndex + 1).trim();
    const href = normalizeUrl(value);

    if (label.trim().toLowerCase() === "link:" && href) {
      return (
        <li key={key} className="text-sm text-gray-700 leading-relaxed">
          {renderDownloadLink(href, `${key}-download`)}
        </li>
      );
    }

    return (
      <li key={key} className="text-sm text-gray-700 leading-relaxed">
        <span className="font-medium text-gray-900">{label}</span>
        {value ? <>{" "}{formatInline(value, { linkLabel: "Baixar" })}</> : null}
      </li>
    );
  }

  return (
    <li key={key} className="text-sm text-gray-700 leading-relaxed">
      {formatInline(content, { linkLabel: "Baixar" })}
    </li>
  );
}

export default function MemorandoContent({ text, className = "" }) {
  if (!text) return null;

  const lines = String(text).split("\n");
  const elements = [];
  let bulletBuffer = [];

  const flushBullets = () => {
    if (bulletBuffer.length === 0) return;

    elements.push(
      <ul
        key={`bullets-${elements.length}`}
        className="list-disc pl-5 space-y-1.5 mb-3 marker:text-emerald-600"
      >
        {bulletBuffer}
      </ul>,
    );
    bulletBuffer = [];
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushBullets();
      elements.push(<div key={`space-${index}`} className="h-2" aria-hidden />);
      return;
    }

    if (CLIENTE_LINE_PATTERN.test(trimmed)) {
      return;
    }

    if (CHECKLIST_ITEM_PATTERN.test(trimmed)) {
      bulletBuffer.push(renderBulletLine(trimmed, `bullet-${index}`));
      return;
    }

    flushBullets();

    if (MAIN_TITLE_PATTERN.test(trimmed)) {
      return;
    }

    if (isSectionHeading(trimmed)) {
      elements.push(
        <h3
          key={`section-${index}`}
          className="text-sm font-bold text-gray-900 mt-5 mb-2 uppercase tracking-wide border-b border-gray-200 pb-1.5"
        >
          {formatInline(trimmed)}
        </h3>,
      );
      return;
    }

    if (SUBHEADING_PATTERN.test(trimmed)) {
      elements.push(
        <p
          key={`subhead-${index}`}
          className="text-sm font-semibold text-gray-900 mt-3 mb-1.5"
        >
          {formatInline(trimmed)}
        </p>,
      );
      return;
    }

    if (METADATA_PATTERN.test(trimmed)) {
      elements.push(renderMetadataLine(trimmed, `meta-${index}`));
      return;
    }

    if (SUBITEM_PATTERN.test(trimmed) || ORDINAL_ITEM_PATTERN.test(trimmed)) {
      elements.push(
        <p key={`sub-${index}`} className="text-sm text-gray-700 ml-3 mb-1.5 leading-relaxed">
          {formatInline(trimmed)}
        </p>,
      );
      return;
    }

    elements.push(
      <p key={`p-${index}`} className="text-sm text-gray-700 mb-2 leading-relaxed">
        {formatInline(trimmed)}
      </p>,
    );
  });

  flushBullets();

  return (
    <div className={`memorando-content ${className}`.trim()}>{elements}</div>
  );
}
